import {ParseResult, Schema} from "effect";

import type {Permission} from "../permissionType";
import {PermissionType} from "../permissionType";

// ── Effect Schema surfaces ─────────────────────────────────────────────

/**
 * Effect Schema representation of the new-style document permission strings.
 *
 * Acceptance is identical to the zod {@link DocumentPermission} declared in
 * `../permissionType`; this surface exists so the inverse pair below can be
 * composed into Effect Schema pipelines (`Schema.encode`, `Schema.decode`,
 * struct fields) instead of being scattered as standalone helpers.
 */
export const DocumentPermissionSchema = Schema.Literal("none", "edit", "view", "comment", "withdrawn");

/**
 * Effect Schema representation of the legacy numeric permission enum.
 *
 * Mirrors the runtime values of the {@link PermissionType} TypeScript enum
 * (`none = 0`, `edit = 1`, `view = 2`, `comment = 3`). The TS enum remains
 * the canonical declaration; this schema is the parsing surface used by the
 * inverse pair carrier {@link DocumentPermissionToPermissionType}.
 */
export const PermissionTypeSchema = Schema.Literal(
    PermissionType.none,
    PermissionType.edit,
    PermissionType.view,
    PermissionType.comment,
);

// ── Canonical inverse pair: DocumentPermission ↔ PermissionType ────────

/**
 * `Schema.transformOrFail` bundling the inverse pair
 * `DocumentPermission ↔ PermissionType`.
 *
 * Bundling both directions in a single carrier prevents the two halves from
 * drifting and lets callers compose the conversion into Effect Schema
 * pipelines instead of calling matched standalone helpers.
 *
 * - decode (DocumentPermission → PermissionType): map `"none"`/`"withdrawn"`
 *   → `PermissionType.none`, `"edit"` → `PermissionType.edit`, `"view"` →
 *   `PermissionType.view`, `"comment"` → `PermissionType.comment`. Always
 *   succeeds for any value the source schema accepts.
 * - encode (PermissionType → DocumentPermission): map `PermissionType.none`
 *   → `"none"`, etc. The legacy enum has no `"withdrawn"` variant, so the
 *   canonical inverse of `none` is the string `"none"` — the `"withdrawn"`
 *   distinction is intentionally lossy in this direction (the round-trip
 *   `decode(encode(x)) === x` holds for every `PermissionType`, but the
 *   reverse round-trip collapses `"withdrawn"` to `"none"`). This matches
 *   the asymmetry of the standalone helpers in `../permissionType`.
 */
export const DocumentPermissionToPermissionType = Schema.transformOrFail(
    DocumentPermissionSchema,
    PermissionTypeSchema,
    {
        strict: true,
        decode: (permission) => {
            switch (permission) {
                case "none":
                case "withdrawn":
                    return ParseResult.succeed(PermissionType.none);
                case "edit":
                    return ParseResult.succeed(PermissionType.edit);
                case "view":
                    return ParseResult.succeed(PermissionType.view);
                case "comment":
                    return ParseResult.succeed(PermissionType.comment);
            }
        },
        encode: (permissionType) => {
            switch (permissionType) {
                case PermissionType.none:
                    return ParseResult.succeed("none" as const);
                case PermissionType.edit:
                    return ParseResult.succeed("edit" as const);
                case PermissionType.view:
                    return ParseResult.succeed("view" as const);
                case PermissionType.comment:
                    return ParseResult.succeed("comment" as const);
            }
        },
    },
);

// ── Effect Schema surface for Permission ───────────────────────────────

/**
 * Effect Schema representation of the resolved {@link Permission} type.
 *
 * Excludes `"withdrawn"` — that is a persistence concern, not a resolved
 * permission level.
 */
export const PermissionSchema = Schema.Literal("none", "view", "comment", "edit");

// ── Canonical inverse pair: Permission ↔ PermissionType ────────────────

/**
 * `Schema.transformOrFail` bundling the inverse pair
 * `Permission ↔ PermissionType`.
 *
 * - decode (Permission → PermissionType): `"none"` → 0, `"view"` → 2,
 *   `"comment"` → 3, `"edit"` → 1.
 * - encode (PermissionType → Permission): reverse mapping.
 *
 * This is lossless in both directions — every `Permission` has a unique
 * `PermissionType` and vice versa.
 */
export const PermissionToPermissionType = Schema.transformOrFail(PermissionSchema, PermissionTypeSchema, {
    strict: true,
    decode: (permission: Permission) => {
        switch (permission) {
            case "none":
                return ParseResult.succeed(PermissionType.none);
            case "edit":
                return ParseResult.succeed(PermissionType.edit);
            case "view":
                return ParseResult.succeed(PermissionType.view);
            case "comment":
                return ParseResult.succeed(PermissionType.comment);
        }
    },
    encode: (permissionType) => {
        switch (permissionType) {
            case PermissionType.none:
                return ParseResult.succeed("none" as const);
            case PermissionType.edit:
                return ParseResult.succeed("edit" as const);
            case PermissionType.view:
                return ParseResult.succeed("view" as const);
            case PermissionType.comment:
                return ParseResult.succeed("comment" as const);
        }
    },
});
